import { Fragment, useRef, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  PlusIcon,
  CheckIcon,
  Bars3Icon,
  Bars4Icon,
  Squares2X2Icon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BookmarkIcon,
  ViewColumnsIcon,
  Bars2Icon,
  TrashIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

// ──────────────────────────────────────────────
// DensityToggle
// ──────────────────────────────────────────────
function DensityToggle({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t("Row density")}
      className="inline-flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5"
    >
      <button
        type="button"
        onClick={() => onChange("comfortable")}
        title={t("Comfortable")}
        className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${
          value === "comfortable"
            ? "bg-white text-slate-700 shadow-sm"
            : "text-slate-400 hover:text-slate-600"
        }`}
      >
        <Bars3Icon className="h-[15px] w-[15px]" />
      </button>
      <button
        type="button"
        onClick={() => onChange("compact")}
        title={t("Compact")}
        className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${
          value === "compact"
            ? "bg-white text-slate-700 shadow-sm"
            : "text-slate-400 hover:text-slate-600"
        }`}
      >
        <Bars4Icon className="h-[15px] w-[15px]" />
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────
// ViewTabs (desktop)
// views: [{ id, name, icon?, count? }]
// ──────────────────────────────────────────────
function ViewTabs({ views, activeView, onSelect }) {
  if (!views || views.length === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {views.map((v) => {
        const active = activeView === v.id;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onSelect(v.id, v)}
            className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[13.5px] font-medium whitespace-nowrap transition-colors ${
              active
                ? "bg-slate-900 text-white"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            {v.icon && (
              <v.icon
                className={`h-3.5 w-3.5 flex-shrink-0 ${
                  active ? "text-white/80" : "text-slate-400"
                }`}
              />
            )}
            {v.name}
            {v.count != null && (
              <span
                className={`ml-0.5 tabular-nums text-xs px-1.5 rounded-full ${
                  active
                    ? "bg-white/20 text-white"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {v.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────
// MobileViewTabs — full-width segmented control for reliable touch targets
// ──────────────────────────────────────────────
function MobileViewTabs({ views, activeView, onSelect }) {
  const { t } = useTranslation();
  if (!views || views.length === 0) return null;

  return (
    <div
      role="tablist"
      aria-label={t("table_view.view_tabs")}
      className="grid w-full gap-0.5 rounded-xl bg-slate-100 p-1"
      style={{ gridTemplateColumns: `repeat(${views.length}, minmax(0, 1fr))` }}
    >
      {views.map((v) => {
        const active = activeView === v.id;
        return (
          <button
            key={v.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(v.id, v)}
            className={`relative z-10 inline-flex min-h-11 touch-manipulation select-none items-center justify-center gap-1 rounded-[10px] px-2 text-xs font-semibold transition-colors ${
              active
                ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80"
                : "text-slate-600 active:bg-white/60"
            }`}
          >
            {v.icon && (
              <v.icon
                className={`h-3.5 w-3.5 flex-shrink-0 ${
                  active ? "text-slate-700" : "text-slate-400"
                }`}
              />
            )}
            <span className="truncate">{v.name}</span>
            {v.count != null && (
              <span
                className={`ml-0.5 tabular-nums rounded-full px-1.5 text-[10px] ${
                  active ? "bg-slate-100 text-slate-600" : "text-slate-400"
                }`}
              >
                {v.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────
// GroupControl — dropdown for grouping rows
// groupOptions: [{ value, label }]
// ──────────────────────────────────────────────
function GroupControl({ value, groupOptions = [], onChange }) {
  const { t } = useTranslation();
  const active = value && value !== "none";
  const activeOption = groupOptions.find((o) => o.value === value);
  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg text-[13.5px] font-medium border transition-colors ${
          active
            ? "bg-slate-100 text-slate-800 border-transparent"
            : "text-slate-500 border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <Squares2X2Icon className="h-[15px] w-[15px]" />
        {active && activeOption ? (
          <>
            {t("Grouped")}:{" "}
            <span className="font-semibold">{activeOption.label}</span>
          </>
        ) : (
          t("Group")
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-50 mt-1 w-52 origin-top-left rounded-xl bg-white py-1.5 shadow-lg ring-1 ring-slate-200 focus:outline-none">
          <div className="px-3 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
            {t("Group by")}
          </div>
          <Menu.Item>
            {({ active: a }) => (
              <button
                type="button"
                onClick={() => onChange("none")}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 ${
                  a ? "bg-slate-50" : ""
                }`}
              >
                <span className="flex-1 text-left">{t("No grouping")}</span>
                {(!value || value === "none") && (
                  <CheckIcon className="h-4 w-4 text-blue-500" />
                )}
              </button>
            )}
          </Menu.Item>
          {groupOptions.map((opt) => (
            <Menu.Item key={opt.value}>
              {({ active: a }) => (
                <button
                  type="button"
                  onClick={() => onChange(opt.value)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 ${
                    a ? "bg-slate-50" : ""
                  }`}
                >
                  <span className="flex-1 text-left">{opt.label}</span>
                  {value === opt.value && (
                    <CheckIcon className="h-4 w-4 text-blue-500" />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

// ──────────────────────────────────────────────
// SortControl — dropdown for column sort
// sortFields: [{ key, label }]
// sort: { field, dir } | null
// ──────────────────────────────────────────────
function SortControl({ sort, sortFields = [], onChange }) {
  const { t } = useTranslation();
  const active = !!sort;
  const activeField = sortFields.find((f) => f.key === sort?.field);
  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg text-[13.5px] font-medium border transition-colors ${
          active
            ? "bg-slate-100 text-slate-800 border-transparent"
            : "text-slate-500 border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        {sort?.dir === "asc" ? (
          <ArrowUpIcon className="h-[15px] w-[15px]" />
        ) : sort?.dir === "desc" ? (
          <ArrowDownIcon className="h-[15px] w-[15px]" />
        ) : (
          <ChevronDownIcon className="h-[15px] w-[15px]" />
        )}
        {active && activeField ? (
          <>
            {t("Sort")}:{" "}
            <span className="font-semibold">{activeField.label}</span>
          </>
        ) : (
          t("Sort")
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-50 mt-1 w-52 origin-top-left rounded-xl bg-white py-1.5 shadow-lg ring-1 ring-slate-200 focus:outline-none">
          <div className="px-3 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
            {t("Sort by")}
          </div>
          {sortFields.map((f) => (
            <Menu.Item key={f.key}>
              {({ active: a }) => (
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      field: f.key,
                      dir:
                        sort?.field === f.key && sort.dir === "asc"
                          ? "desc"
                          : "asc",
                    })
                  }
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 ${
                    a ? "bg-slate-50" : ""
                  }`}
                >
                  <span className="flex-1 text-left">{f.label}</span>
                  {sort?.field === f.key && (
                    <span className="text-blue-500">
                      {sort.dir === "asc" ? (
                        <ArrowUpIcon className="h-4 w-4" />
                      ) : (
                        <ArrowDownIcon className="h-4 w-4" />
                      )}
                    </span>
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
          {sort && (
            <>
              <div className="my-1.5 h-px bg-slate-100" />
              <Menu.Item>
                {({ active: a }) => (
                  <button
                    type="button"
                    onClick={() => onChange(null)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-500 ${
                      a ? "bg-slate-50" : ""
                    }`}
                  >
                    {t("Remove sort")}
                  </button>
                )}
              </Menu.Item>
            </>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

// ──────────────────────────────────────────────
// ColumnsControl — show/hide + drag-reorder columns
// columns: [{ key, label, always }]
// visibleKeys / order: arrays of keys
// ──────────────────────────────────────────────
function ColumnsControl({ columns = [], order = [], visibleKeys = [], onToggle, onReorder, onReset }) {
  const { t } = useTranslation();
  const dragIdx = useRef(null);
  const [over, setOver] = useState(null);
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const ordered = (order.length ? order : columns.map((c) => c.key)).filter((k) => byKey.has(k));

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg text-[13.5px] font-medium border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50">
        <ViewColumnsIcon className="h-[15px] w-[15px]" />
        {t("table_view.columns")}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-1 w-64 origin-top-right rounded-xl bg-white py-1.5 shadow-lg ring-1 ring-slate-200 focus:outline-none">
          <div className="flex items-center justify-between px-3 py-1">
            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
              {t("table_view.show_reorder_columns")}
            </span>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                className="text-[11px] font-medium text-blue-500 hover:text-blue-600"
              >
                {t("Reset")}
              </button>
            )}
          </div>
          <div className="max-h-[340px] overflow-y-auto px-1">
            {ordered.map((key, i) => {
              const col = byKey.get(key);
              if (!col) return null;
              const on = visibleKeys.includes(key);
              return (
                <div
                  key={key}
                  draggable={!col.always}
                  onDragStart={() => { dragIdx.current = i; }}
                  onDragOver={(e) => { e.preventDefault(); setOver(i); }}
                  onDragEnd={() => {
                    if (dragIdx.current != null && over != null && dragIdx.current !== over) {
                      onReorder?.(dragIdx.current, over);
                    }
                    dragIdx.current = null;
                    setOver(null);
                  }}
                  onClick={() => !col.always && onToggle?.(key)}
                  className={`flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm ${
                    col.always ? "cursor-default" : "cursor-pointer hover:bg-slate-50"
                  }`}
                  style={{ borderTop: over === i ? "2px solid rgb(59 130 246)" : "2px solid transparent" }}
                >
                  <span
                    className={`grid h-[16px] w-[16px] flex-shrink-0 place-items-center rounded-[4px] border ${
                      on ? "border-blue-500 bg-blue-500 text-white" : "border-slate-300 bg-white"
                    }`}
                  >
                    {on && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className={`flex-1 ${col.always ? "text-slate-400" : "text-slate-700"}`}>
                    {col.label}
                  </span>
                  {col.always ? (
                    <span className="text-[11px] text-slate-300">{t("table_view.locked")}</span>
                  ) : (
                    <Bars2Icon className="h-[15px] w-[15px] cursor-grab text-slate-300" />
                  )}
                </div>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

// ──────────────────────────────────────────────
// SavedViews — user-defined views with dirty state
// ──────────────────────────────────────────────
function SavedViews({ views = [], activeId, dirty, onApply, onSaveAs, onUpdate, onDelete }) {
  const { t } = useTranslation();
  const promptSaveAs = () => {
    const name = (window.prompt(t("table_view.view_name_prompt"), t("table_view.new_view")) || "").trim();
    if (name) onSaveAs?.(name);
  };
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {views.map((v) => {
        const active = activeId === v.id;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onApply?.(v.id)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors ${
              active ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            <BookmarkIcon className={`h-3.5 w-3.5 ${active ? "text-white/80" : "text-slate-400"}`} />
            {v.name}
            {active && dirty && (
              <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-amber-300" title={t("table_view.unsaved_changes")} />
            )}
          </button>
        );
      })}

      {activeId && dirty && (
        <Menu as="div" className="relative">
          <Menu.Button className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[13px] font-medium text-blue-600 hover:bg-blue-50">
            <BookmarkIcon className="h-3.5 w-3.5" />
            {t("Save")}
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-0 z-50 mt-1 w-56 origin-top-left rounded-xl bg-white py-1.5 shadow-lg ring-1 ring-slate-200 focus:outline-none">
              <Menu.Item>
                {({ active: a }) => (
                  <button type="button" onClick={onUpdate} className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 ${a ? "bg-slate-50" : ""}`}>
                    <ArrowPathIcon className="h-4 w-4 text-slate-400" />
                    {t("table_view.update_view")}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active: a }) => (
                  <button type="button" onClick={promptSaveAs} className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 ${a ? "bg-slate-50" : ""}`}>
                    <BookmarkIcon className="h-4 w-4 text-slate-400" />
                    {t("table_view.save_as_new")}
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      )}

      {activeId && (
        <button
          type="button"
          onClick={() => onDelete?.(activeId)}
          title={t("table_view.delete_view")}
          className="grid h-7 w-7 place-items-center rounded-full text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </button>
      )}

      <button
        type="button"
        onClick={promptSaveAs}
        title={t("table_view.new_view")}
        className="grid h-7 w-7 place-items-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────
// TableToolbar — main export
//
// Props:
//   views          [{ id, name, icon?, count? }]  — predefined view tabs
//   activeView     string | null                  — current active view id
//   onViewChange   (id, viewObj) => void
//   groupOptions   [{ value, label }]             — options for GroupControl
//   activeGroup    string                         — current group field ('none' = off)
//   onGroupChange  (value) => void
//   sort           { field, dir } | null
//   sortFields     [{ key, label }]
//   onSortChange   (sort | null) => void
//   density        'comfortable' | 'compact'
//   onDensityChange (value) => void
//   rightControls  ReactNode                      — extra controls slot (right side)
// ──────────────────────────────────────────────
export const TableToolbar = ({
  views = [],
  activeView = null,
  onViewChange,
  groupOptions = [],
  activeGroup = "none",
  onGroupChange,
  sort = null,
  sortFields = [],
  onSortChange,
  density = "comfortable",
  onDensityChange,
  rightControls = null,
  // Column show/hide/reorder
  columns = [],
  visibleKeys = [],
  columnOrder = [],
  onToggleColumn,
  onReorderColumn,
  onResetColumns,
  // Saved views
  savedViews = [],
  activeSavedViewId = null,
  savedViewDirty = false,
  onApplySavedView,
  onSaveAsView,
  onUpdateView,
  onDeleteView,
}) => {
  const showGroupControl = groupOptions.length > 0 && onGroupChange;
  const showSortControl = sortFields.length > 0 && onSortChange;
  const showDensity = !!onDensityChange;
  const showColumns = columns.length > 0 && onToggleColumn;
  const showSavedViews = !!onSaveAsView;

  const hasViewTabs = views.length > 0;

  return (
    <div className="border-b border-slate-200">
      {/* Mobile: dedicated row for view tabs — avoids overlap with toolbar controls.
          Local z-[1] only; do not isolate this bar or column menus paint under the table. */}
      {hasViewTabs && (
        <div className="relative z-[1] px-2 pb-1 pt-2 md:hidden">
          <MobileViewTabs
            views={views}
            activeView={activeView}
            onSelect={onViewChange || (() => {})}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 px-3 py-2 max-md:gap-1.5 max-md:px-2 max-md:py-1.5 md:px-4 md:py-2.5">
        {/* Desktop: view tabs + saved views */}
        {hasViewTabs && (
          <div className="hidden md:block">
            <ViewTabs
              views={views}
              activeView={activeView}
              onSelect={onViewChange || (() => {})}
            />
          </div>
        )}
        {showSavedViews && (
          <>
            {hasViewTabs && <div className="mx-1 hidden h-5 w-px bg-slate-200 md:block" />}
            <div className="hidden md:block">
              <SavedViews
                views={savedViews}
                activeId={activeSavedViewId}
                dirty={savedViewDirty}
                onApply={onApplySavedView}
                onSaveAs={onSaveAsView}
                onUpdate={onUpdateView}
                onDelete={onDeleteView}
              />
            </div>
          </>
        )}

        <div className="hidden min-w-0 flex-1 md:block" />

        {/* Right: controls */}
        <div className="flex items-center gap-1.5 max-md:ml-auto">
          {rightControls}
          {showGroupControl && (
            <GroupControl
              value={activeGroup}
              groupOptions={groupOptions}
              onChange={onGroupChange}
            />
          )}
          {showSortControl && (
            <SortControl
              sort={sort}
              sortFields={sortFields}
              onChange={onSortChange}
            />
          )}
          {showColumns && (
            <ColumnsControl
              columns={columns}
              order={columnOrder}
              visibleKeys={visibleKeys}
              onToggle={onToggleColumn}
              onReorder={onReorderColumn}
              onReset={onResetColumns}
            />
          )}
          {showDensity && (
            <div className="max-md:hidden">
              <DensityToggle value={density} onChange={onDensityChange} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
